import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { resolveTip } from './tips';
import './tooltip.css';

// One tooltip for the tool pages (mounted by ProtectedRoute, so the public
// site never gets it), driven by event delegation on `document`:
// any element with `data-tip` shows a tooltip on hover (after a short delay)
// or on keyboard focus (immediately). Nothing needs wrapping — add the
// attribute and the text key (see tips.js).
//
// Hides on: pointer leaving the element, focus leaving, Escape, any
// pointerdown (so a click or a touch tap never leaves a tooltip behind),
// scroll and resize.

const SHOW_DELAY = 450;   // ms of hover before the tip appears
const WARM_DELAY = 80;    // ...unless another tip was just shown
const WARM_WINDOW = 500;
const GAP = 8;            // px between element and tip
const MARGIN = 8;         // px kept clear of the viewport edge
const TIP_ID = 'ut-tooltip';

const findTarget = (node) =>
  node instanceof Element ? node.closest('[data-tip]') : null;

const isKeyboardFocus = (el) => {
  try { return el.matches(':focus-visible'); } catch { return true; }
};

export default function TooltipLayer() {
  const [tip, setTip] = useState(null);      // { target, text }
  const [style, setStyle] = useState(null);  // null until measured
  const [place, setPlace] = useState('below');
  const boxRef = useRef(null);
  const timerRef = useRef(null);
  const currentRef = useRef(null);
  const lastHideRef = useRef(0);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
    const hide = () => {
      clearTimer();
      if (currentRef.current) {
        lastHideRef.current = Date.now();
        currentRef.current = null;
        setTip(null);
      }
    };
    const show = (target, immediate) => {
      clearTimer();
      const text = resolveTip(target);
      if (!text) return;
      const warm = Date.now() - lastHideRef.current < WARM_WINDOW;
      const delay = immediate ? 0 : (warm ? WARM_DELAY : SHOW_DELAY);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        currentRef.current = target;
        setTip({ target, text });
      }, delay);
    };

    const onOver = (e) => {
      const t = findTarget(e.target);
      if (!t) return;
      if (t === currentRef.current) return;
      show(t, false);
    };
    const onOut = (e) => {
      const t = findTarget(e.target);
      if (!t) return;
      if (e.relatedTarget instanceof Node && t.contains(e.relatedTarget)) return;
      hide();
    };
    const onFocusIn = (e) => {
      const t = findTarget(e.target);
      if (!t || !isKeyboardFocus(t)) return;
      show(t, true);
    };
    const onFocusOut = (e) => {
      if (findTarget(e.target)) hide();
    };
    const onKey = (e) => { if (e.key === 'Escape') hide(); };

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('scroll', hide, { capture: true, passive: true });
    window.addEventListener('resize', hide);
    return () => {
      clearTimer();
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('scroll', hide, { capture: true });
      window.removeEventListener('resize', hide);
    };
  }, []);

  // Measure and place: below the element, above if there is no room below.
  useLayoutEffect(() => {
    if (!tip) { setStyle(null); return; }
    const box = boxRef.current;
    if (!box || !document.contains(tip.target)) { setTip(null); return; }
    const r = tip.target.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const fitsBelow = r.bottom + GAP + b.height <= vh - MARGIN;
    const fitsAbove = r.top - GAP - b.height >= MARGIN;
    const below = fitsBelow || !fitsAbove;
    const top = below ? r.bottom + GAP : r.top - GAP - b.height;
    let left = r.left + r.width / 2 - b.width / 2;
    left = Math.max(MARGIN, Math.min(left, vw - MARGIN - b.width));
    const arrowX = Math.max(12, Math.min(r.left + r.width / 2 - left, b.width - 12));
    setPlace(below ? 'below' : 'above');
    setStyle({ top: `${top}px`, left: `${left}px`, '--tip-arrow-x': `${arrowX}px` });
  }, [tip]);

  // Screen readers: describe the element by the visible tooltip.
  useEffect(() => {
    if (!tip) return undefined;
    const el = tip.target;
    const had = el.getAttribute('aria-describedby');
    el.setAttribute('aria-describedby', had ? `${had} ${TIP_ID}` : TIP_ID);
    return () => {
      if (had) el.setAttribute('aria-describedby', had);
      else el.removeAttribute('aria-describedby');
    };
  }, [tip]);

  if (!tip) return null;
  return createPortal(
    <div
      ref={boxRef}
      id={TIP_ID}
      role="tooltip"
      className={`ut-tip ${place}${style ? ' is-placed' : ''}`}
      style={style || { top: 0, left: 0 }}
    >
      {tip.text}
    </div>,
    document.body
  );
}
